/**
 * Phase 1.5.4 — 休業日追加モーダル（admin SPA）。
 *
 * 営業日設定画面から呼ばれるモーダル。日付・種別（kind）・ラベル（任意）を入力して
 * POST /api/admin/business-settings/holidays で UPSERT する。
 */

import { upsertHolidays, type HolidayDto } from "../api";
import { showError, showToast } from "../render";

const KIND_OPTIONS: Array<{ value: HolidayDto["kind"]; label: string }> = [
  { value: "temporary", label: "臨時休業" },
  { value: "custom", label: "定例休" },
  { value: "national", label: "国民の祝日" },
];

/**
 * 休業日追加モーダルを開く。
 *
 * @param onSuccess 追加成功時のコールバック（呼び出し側が再描画する）
 */
export function openHolidayAddModal(onSuccess: () => void | Promise<void>): void {
  closeHolidayAddModal();

  // 既定の date 値は今日（YYYY-MM-DD・JST）
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });

  const backdrop = document.createElement("div");
  backdrop.id = "modal-holiday-add";
  backdrop.className =
    "fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4";
  backdrop.innerHTML = modalHtml(today);
  document.body.appendChild(backdrop);

  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) closeHolidayAddModal();
  });

  document.getElementById("btn-holiday-close")?.addEventListener("click", closeHolidayAddModal);
  document.getElementById("btn-holiday-cancel")?.addEventListener("click", closeHolidayAddModal);

  document
    .getElementById("btn-holiday-submit")
    ?.addEventListener("click", async () => {
      const dateEl = document.getElementById("holiday-date") as HTMLInputElement | null;
      const kindEl = document.getElementById("holiday-kind") as HTMLSelectElement | null;
      const labelEl = document.getElementById("holiday-label") as HTMLInputElement | null;
      if (!dateEl || !kindEl) return;

      const date = dateEl.value;
      const kind = kindEl.value as HolidayDto["kind"];
      const label = (labelEl?.value ?? "").slice(0, 50);

      if (!date) {
        showError("日付を入力してください");
        return;
      }

      const submitBtn = document.getElementById("btn-holiday-submit") as HTMLButtonElement | null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "登録中...";
      }

      try {
        await upsertHolidays([{ date, kind, label: label || null }]);
        closeHolidayAddModal();
        showToast("休業日を登録しました", "success");
        await onSuccess();
      } catch (e: any) {
        showError(`登録に失敗しました：${e?.message ?? e}`);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = "登録する";
        }
      }
    });
}

export function closeHolidayAddModal(): void {
  document.getElementById("modal-holiday-add")?.remove();
}

function modalHtml(defaultDate: string): string {
  const optionsHtml = KIND_OPTIONS.map(
    (opt) =>
      `<option value="${escapeHtml(opt.value)}">${escapeHtml(opt.label)}</option>`
  ).join("");
  return `
    <div class="bg-white rounded-lg w-full max-w-md p-5 shadow-xl"
      role="dialog" aria-modal="true" aria-labelledby="holiday-modal-title">
      <div class="flex items-start justify-between mb-3">
        <h2 id="holiday-modal-title" class="text-lg font-bold text-gray-900">
          休業日を追加
        </h2>
        <button id="btn-holiday-close"
          class="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2"
          aria-label="閉じる">
          ×
        </button>
      </div>

      <label class="block mb-3">
        <span class="text-sm font-semibold text-gray-700">日付</span>
        <input type="date" id="holiday-date" value="${escapeHtml(defaultDate)}"
          class="w-full border border-gray-300 rounded px-2 py-2 text-base mt-1">
      </label>

      <label class="block mb-3">
        <span class="text-sm font-semibold text-gray-700">種別</span>
        <select id="holiday-kind"
          class="w-full border border-gray-300 rounded px-2 py-2 text-base mt-1">
          ${optionsHtml}
        </select>
      </label>

      <label class="block mb-4">
        <span class="text-sm font-semibold text-gray-700">ラベル（任意・50字以内）</span>
        <input type="text" id="holiday-label" maxlength="50"
          placeholder="例：年末休み、夏季休暇"
          class="w-full border border-gray-300 rounded px-2 py-2 text-base mt-1">
      </label>

      <div class="flex gap-2 justify-end">
        <button id="btn-holiday-cancel"
          class="px-4 py-3 bg-gray-100 text-gray-700 rounded">
          キャンセル
        </button>
        <button id="btn-holiday-submit"
          class="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold">
          登録する
        </button>
      </div>
    </div>
  `;
}

function escapeHtml(s: unknown): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
